import React, { useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Clear as ClearIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import { SvgIcon } from "@mui/material";
import { styled } from "@mui/material/styles";
import DownloadDataButton from "../common/DownloadDataButton";
import { usePermission } from "../../hooks/usePermission";

const CalendarIcon = () => (
  <SvgIcon sx={{ width: "15px", height: "16px" }}>
    <svg
      width="15"
      height="16"
      viewBox="0 0 15 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.25 14.25V3.16667C14.25 2.29346 13.5399 1.58333 12.6667 1.58333H11.0833V0H9.5V1.58333H4.75V0H3.16667V1.58333H1.58333C0.710125 1.58333 0 2.29346 0 3.16667V14.25C0 15.1232 0.710125 15.8333 1.58333 15.8333H12.6667C13.5399 15.8333 14.25 15.1232 14.25 14.25ZM4.75 12.6667H3.16667V11.0833H4.75V12.6667ZM4.75 9.5H3.16667V7.91667H4.75V9.5ZM7.91667 12.6667H6.33333V11.0833H7.91667V12.6667ZM7.91667 9.5H6.33333V7.91667H7.91667V9.5ZM11.0833 12.6667H9.5V11.0833H11.0833V12.6667ZM11.0833 9.5H9.5V7.91667H11.0833V9.5ZM12.6667 5.54167H1.58333V3.95833H12.6667V5.54167Z"
        fill="#183059"
      />
    </svg>
  </SvgIcon>
);

const DropdownIcon = (props) => (
  <SvgIcon
    {...props}
    viewBox="0 0 18 10"
    sx={{
      width: "18px",
      height: "10px",
      color: "#183059 !important",
      "& path": {
        stroke: "#183059 !important",
      },
    }}
  >
    <path
      d="M1 1L9 9L17 1"
      stroke="#183059"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </SvgIcon>
);

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: "35px",
    borderRadius: "4px",
    backgroundColor: "#F2F2F3",
    border: "1px solid #414958",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 16px",
    "&::-webkit-calendar-picker-indicator": {
      display: "none",
    },
    "&::-webkit-inner-spin-button": {
      display: "none",
    },
  },
  "& .MuiInputLabel-outlined": {
    transform: "translate(16px, 8px) scale(1)",
  },
  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
    transform: "translate(14px, -6px) scale(0.75)",
    backgroundColor: "#F2F2F3",
    padding: "0 4px",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  height: "35px",
  borderRadius: "4px",
  backgroundColor: "#F2F2F3",
  border: "1px solid #414958",
  width: "180px",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": {
    padding: "8px 16px",
    paddingRight: "40px !important",
    whiteSpace: "nowrap",
  },
  "& .MuiSelect-icon": {
    right: "10px",
    top: "50%",
    marginTop: 0,
    transform: "translateY(-50%)",
    color: "#183059 !important",
  },
  "& .MuiSelect-icon.MuiSelect-iconOpen": {
    transform: "translateY(-50%) rotate(180deg)",
  },
  "& .MuiSelect-icon path": {
    stroke: "#183059 !important",
    fill: "none !important",
  },
  [theme.breakpoints.down("md")]: { width: "100%" },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: "180px",
  "& .MuiInputLabel-outlined": {
    transform: "translate(16px, 8px) scale(1)",
  },
  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
    transform: "translate(14px, -6px) scale(0.75)",
    backgroundColor: "#F2F2F3",
    padding: "0 4px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const isDateFilter = (filter) =>
  filter.type === "date" || filter.id.toLowerCase().includes("date");

export default function SearchFilterWithDatePicker({
  filters = [],
  searchPlaceholder = "search",
  onSearchChange,
  onFilterChange,
  userDateFormat = "dd/MM/yyyy",
  dataDownload = true,
  downloadRows = [],
  downloadColumns = [],
  downloadFileName = "download-data",
  onDownload,
  downloadDisabled = false,
  downloadTooltipTitle,
  selectedCount = 0,
}) {
  const { t } = useTranslation();
  const { hasDownloadPermission } = usePermission();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const canUserDownload = hasDownloadPermission(downloadFileName);
  const [searchValue, setSearchValue] = useState("");
  const [filterValues, setFilterValues] = useState(
    filters.reduce((acc, f) => ({ ...acc, [f.id]: null }), {})
  );

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchValue(v);
    onSearchChange?.(v);
  };

  const handleFilterChange = (filterId, value) => {
    setFilterValues((prev) => {
      const next = { ...prev, [filterId]: value };
      onFilterChange?.(next);
      return next;
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? "12px" : "20px",
          width: "100%",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: isMobile ? "100%" : "320px" }}>
          <StyledTextField
            placeholder={t(searchPlaceholder)}
            value={searchValue}
            onChange={handleSearchChange}
            fullWidth
            InputProps={{
              endAdornment: <SearchIcon sx={{ color: "#183059", mr: "10px" }} />,
            }}
            sx={{ "& input::placeholder": { color: "#1F242E", opacity: 1 } }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: "12px",
            alignItems: "center",
            width: isMobile ? "100%" : "auto",
          }}
        >
          {filters.map((filter) => {
            if (isDateFilter(filter)) {
              return (
                <Box
                  key={filter.id}
                  sx={{
                    border: "1px solid #414958",
                    backgroundColor: "#F2F2F3",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    height: "35px",
                    width: "180px",
                    [theme.breakpoints.down("md")]: { width: "100%" },
                  }}
                >
                  <DatePicker
                    value={filterValues[filter.id]}
                    onChange={(v) => handleFilterChange(filter.id, v)}
                    format={userDateFormat}
                    slots={{ openPickerIcon: CalendarIcon }}
                    slotProps={{
                      textField: {
                        placeholder: t(filter.label || "selectDate"),
                        autoFocus: false,
                        sx: {
                          flex: 1,
                          "& .MuiOutlinedInput-root": {
                            height: "35px",
                            borderRadius: "4px",
                            fontSize: "0.9rem",
                            paddingRight: "8px",
                            paddingLeft: "16px",
                            paddingTop: 0,
                            paddingBottom: 0,
                            "& input": {
                              fontSize: "0.9rem",
                              padding: 0,
                            },
                            "& fieldset": { border: "none" },
                          },
                          "& .MuiInputAdornment-root": {
                            marginTop: 0,
                            marginBottom: 0,
                            marginLeft: "8px",
                            marginRight: 0,
                          },
                        },
                      },
                    }}
                  />
                  {filterValues[filter.id] && (
                    <IconButton
                      onClick={() => handleFilterChange(filter.id, null)}
                      sx={{ color: "#999", height: "24px", width: "24px", padding: "4px", mr: "8px" }}
                      title={t("clearDate", "Clear date")}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              );
            }
            return (
              <StyledFormControl key={filter.id}>
                <InputLabel>{t(filter.label)}</InputLabel>
                <StyledSelect
                  value={filterValues[filter.id] || ""}
                  label={filter.label}
                  onChange={(e) => handleFilterChange(filter.id, e.target.value)}
                  IconComponent={DropdownIcon}
                >
                  <MenuItem value="">
                    <em>{t("none")}</em>
                  </MenuItem>
                  {filter.options?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </StyledFormControl>
            );
          })}
          {dataDownload && canUserDownload && selectedCount > 0 && (
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#0284c7",
                background: "#e0f2fe",
                padding: "3px 8px",
                borderRadius: "12px",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {selectedCount} {t("selected")}
            </Box>
          )}
          {dataDownload && canUserDownload && (
            <DownloadDataButton
              headers={downloadColumns}
              data={downloadRows}
              onDownload={onDownload}
              filename={downloadFileName}
              disabled={downloadDisabled}
              tooltipTitle={downloadTooltipTitle}
            />
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
